import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { formatDateLocal } from "components/Common/commonFunctions";
import RightPanelCard from "components/Common/RightPanelCard";
import NotificationAlert from "react-notification-alert";
import {
  addTechnicianLeave,
  getTechnicianLeave,
  editTechnicianLeave,
} from "actions/technicianLeave";
import { useDispatch } from "react-redux";

const initialFields = {
  requestedOn: {
    value: "",
    error: "",
  },
  startDate: {
    value: "",
    error: "",
  },
  endDate: {
    value: "",
    error: "",
  },
  reason: {
    value: "",
    error: "",
  },
};

export const TechnicianLeaveModal = ({
  isOpen,
  mode,
  data,
  onClose,
  technicianID,
}) => {
  const dispatch = useDispatch();
  const notificationAlertRef = useRef(null);
  const [technicianLeaveData, setTechnicianLeaveData] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);

  useEffect(() => {
    if (mode === "EDIT" && data) {
      // auto fill data for edit mode
      setTechnicianLeaveData({
        requestedOn: {
          value: formatDateLocal(new Date(data.requestedOn)),
          error: "",
        },
        startDate: {
          value: formatDateLocal(new Date(data.startDate)),
          error: "",
        },
        endDate: { value: formatDateLocal(new Date(data.endDate)), error: "" },
        reason: { value: data.reason || "", error: "" },
      });
    } else {
      // reset form for add mode or when modal is closed
      setTechnicianLeaveData(initialFields);
    }
  }, [mode, data, isOpen]);

  const handleInputChange = (fieldName, newValue) => {
    setTechnicianLeaveData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "requestedOn":
        return !value ? "Requested On is required" : "";

      case "startDate":
        return !value ? "Start Date is required" : "";

      case "endDate":
        return !value
          ? "End Date is required"
          : technicianLeaveData.startDate.value &&
            value < technicianLeaveData.startDate.value
          ? "End Date cannot be before Start Date"
          : "";

      case "reason":
        return !value ? "Reason is required" : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setTechnicianLeaveData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const validateForm = () => {
    let newTechnician = { ...technicianLeaveData };

    //get all the keys in array
    Object.keys(newTechnician).forEach((key) => {
      newTechnician[key] = {
        ...newTechnician[key],
        error: getValidationError(key, newTechnician[key].value),
      };
    });
    setTechnicianLeaveData(newTechnician); //if error set error state
    return Object.values(newTechnician).every((item) => item.error === "");
  };

  const handleSave = async () => {
    if (validateForm()) {
      try {
        setShowLoaderOnClick(true);

        const bodyData = {
          technicianID: technicianID,
          requestedOn: technicianLeaveData.requestedOn.value,
          startDate: technicianLeaveData.startDate.value,
          endDate: technicianLeaveData.endDate.value,
          reason: technicianLeaveData.reason.value,
        };

        const res = await addTechnicianLeave(bodyData);
        if (res.code === 200) {
          notify("success", res.message || "Leave added successfully");
          onClose();

          getTechnicianLeave(technicianID, dispatch);
        } else {
          const message = Array.isArray(res?.message)
            ? res.message
                .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
            : res?.message || "Failed to add leave";
          notify("danger", message);
        }
      } catch (error) {
        notify("danger", "failed to add leave!");
      } finally {
        setShowLoaderOnClick(false);
      }
    }
  };

  const handleEdit = async () => {
    if (!validateForm()) return;

    setShowLoaderOnClick(true);
    const bodyData = {
      requestedOn: technicianLeaveData.requestedOn.value,
      startDate: technicianLeaveData.startDate.value,
      endDate: technicianLeaveData.endDate.value,
      reason: technicianLeaveData.reason.value,
    };
    try {
      const res = await editTechnicianLeave(data.ID, technicianID, bodyData);
      if (res.code === 200) {
        notify("success", res.message || "Leave updated successfully");
        onClose();
        getTechnicianLeave(technicianID, dispatch);
      } else {
        notify("danger", res.message || "Failed to update leave");
      }
    } catch (error) {
      notify("danger", "Failed to update leave");
    } finally {
      setShowLoaderOnClick(false);
    }
  };
  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <RightPanelCard isOpen={isOpen} onClose={onClose}>
        <Card>
          <CardHeader>
            {mode === "EDIT" ? "Edit Leave Request" : "Add Leave Request"}
          </CardHeader>

          <CardBody>
            <Row>
              <Col md="4" className="mb-2">
                <FormGroup
                  className={`has-label ${
                    technicianLeaveData.requestedOn.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Requested On <span className="validation-error">*</span>
                  </Label>
                  <Input
                    id="requestedOn"
                    name="requestedOn"
                    type="date"
                    placeholder="Select Request date"
                    value={technicianLeaveData.requestedOn.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  />
                  {technicianLeaveData.requestedOn.error && (
                    <small className="validation-error">
                      {technicianLeaveData.requestedOn.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="4" className="mb-2">
                <FormGroup
                  className={`has-label ${
                    technicianLeaveData.startDate.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Start Date <span className="validation-error">*</span>
                  </Label>
                  <Input
                    id="startDate"
                    name="startDate"
                    type="date"
                    placeholder="Select start date"
                    value={technicianLeaveData.startDate.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  />
                  {technicianLeaveData.startDate.error && (
                    <small className="validation-error">
                      {technicianLeaveData.startDate.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="4" className="mb-2">
                <FormGroup
                  className={`has-label ${
                    technicianLeaveData.endDate.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    End Date <span className="validation-error">*</span>
                  </Label>
                  <Input
                    id="endDate"
                    name="endDate"
                    type="date"
                    placeholder="Select End date"
                    value={technicianLeaveData.endDate.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    min={technicianLeaveData.startDate.value}
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  />
                  {technicianLeaveData.endDate.error && (
                    <small className="validation-error">
                      {technicianLeaveData.endDate.error}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="12" className="mb-3">
                <FormGroup
                  className={`has-label ${
                    technicianLeaveData.reason.error ? "has-danger" : ""
                  } d-flex flex-column align-items-start`}
                >
                  <Label>
                    Reason :<span className="validation-error">*</span>
                  </Label>
                  <Input
                    name="reason"
                    type="textarea"
                    placeholder="Enter Reason for leave"
                    autoComplete="off"
                    value={technicianLeaveData.reason.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    maxLength={500}
                  />
                  {technicianLeaveData.reason.error && (
                    <small className="validation-error">
                      {technicianLeaveData.reason.error}
                    </small>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
          <CardFooter>
            <div className="d-flex justify-content-end mb-0">
              <Button color="secondary" onClick={onClose} className="mr-2">
                Cancel
              </Button>
              <Button
                color="default"
                onClick={() => (mode === "EDIT" ? handleEdit() : handleSave())}
                disabled={Object.values(technicianLeaveData).some(
                  (item) => item.error !== ""
                )}
              >
                {showLoaderOnClick ? (
                  <i className="fa fa-spinner fa-spin fa-lg text-white" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </RightPanelCard>
    </>
  );
};

export default TechnicianLeaveModal;
